(ns machtfit.startpage.header
  (:require [clojure.string :as str]
            [machtfit.app.routes :as routes]
            [machtfit.app.tracking :as tracking]
            [machtfit.core :as machtfit]
            [machtfit.feature :as feature]
            [machtfit.format :as format]
            [machtfit.startpage.events :as events]
            [machtfit.startpage.global-search :as global-search]
            [machtfit.startpage.icons :as icons]
            [machtfit.startpage.layout :as layout]
            [machtfit.startpage.navigation :as navigation]
            [machtfit.startpage.statics :as statics]
            [machtfit.translation :refer [t]]
            [machtfit.widgets.icon :as icon]
            [re-frame.core :as rf]
            [reagent.core :as r]))

(rf/reg-sub ::remaining-sachbezug-budget
  :<- [:get [:user :remaining-cap]]
  :<- [:get [:user :balance]]
  (fn [[remaining-cap balance] _]
    (when remaining-cap
      (min remaining-cap balance))))

(defn- user-menu-link [page-key & {:keys [featured?]}]
  (let [{:keys [nav-image-url url path route-data target] entry-name :name} (get statics/pages page-key)
        href @(rf/subscribe [:get [:urls url]])]
    [:a
     (cond-> (when route-data (routes/attrs-link-to-page route-data))
       url (assoc :href href)
       path (assoc :href path)
       featured? (assoc :class "nav-featured-link")
       target (assoc :target target)
       true (assoc :data-ph-capture-attribute-ui_id (tracking/compose-ui-id
                                                     "header"
                                                     "dropdown"
                                                     (name page-key))))

     (when nav-image-url
       [:img {:src nav-image-url}])
     [:span (t entry-name)]]))

(defn- open-profile-page [_event]
  (let [{:keys [url]} (:profile-view statics/external-pages)
        real-url @(rf/subscribe [:get [:urls url]])]
    (rf/dispatch [:redirect real-url])))

(def global-search-bar-ph-ui-id
  "searchbar-global")

(rf/reg-event-fx ::entered-keyword-filter
  (fn [{:keys [db]} [_ value area]]
    (events/update-filter db
                          #(-> %
                               (assoc :keyword value)
                               ;; we remove filters that,
                               ;; in combination with a keyword,
                               ;; would make zero results too likely.
                               (dissoc :categories)
                               (dissoc :favorites)
                               (assoc :area area)))))

(rf/reg-event-fx ::search-globally
  (fn [{:keys [db]} [_ search-string area]]
    (let [is-spa? (get db :is-spa?)
          in-catalog? (= :catalog (get-in db [:ui :page :id]))
          ;; The exact-vs-relaxed split is only known once the server response
          ;; settles, so the finer value rides the later `offers_filtered` event.
          properties {"search-string" search-string
                      "search-engine" "postgres-fts"}]
      (if-let [url (global-search/cross-area-search-url db search-string area)]
        {::tracking/capture-ui-event [db "searched_globally" global-search-bar-ph-ui-id
                                      properties tracking/before-navigation]
         :redirect {:url url}}
        (merge {::tracking/capture-ui-event [db "searched_globally" global-search-bar-ph-ui-id
                                             properties]}
               (if is-spa?
                 {:dispatch-n [(when-not in-catalog?
                                 [::events/navigate-to-catalog nil nil])
                               [::entered-keyword-filter search-string area]]}
                 {:redirect {:url (routes/page-url
                                   {:id :catalog
                                    :filter (merge {:keyword search-string :area area})})}}))))))

(defn- global-search-bar [_current-filter]
  (let [search-input (r/atom "")
        dropdown-open? (r/atom false)
        mouse-on-dropdown? (r/atom false)
        has-dropdown? (and @(rf/subscribe [:get [:user :has-machtfit-area?]])
                           @(rf/subscribe [:get [:user :has-company-area?]]))]
    (r/create-class
     {:component-did-update
      (fn [this old-argv]
        (let [new-argv (rest (r/argv this))
              new-filter (first new-argv)
              new-keyword (get new-filter :keyword)
              old-keyword (get (second old-argv) :keyword)]
          (when-not (= new-keyword old-keyword)
            (reset! search-input new-keyword))))
      :reagent-render
      (fn [_current-filter]
        [:div.global-filter
         {:on-blur #(when-not @mouse-on-dropdown?
                      (reset! dropdown-open? false))}
         [:div.global-filter-bar
          [:div.global-filter-keyword
           {:data-ph-capture-attribute-ui_id global-search-bar-ph-ui-id}
           [:input
            {:id "global-search-input"
             :type "text"
             :value @search-input
             :auto-complete "off"
             :on-change #(reset! search-input (-> % .-target .-value))
             :on-click #(when (seq @search-input)
                          (reset! dropdown-open? true))
             :aria-label (when @(rf/subscribe [::feature/feature-flag-enabled?
                                               global-search/cross-area-search-flag])
                           (t {:de "Angebote, Firmenbereich und Magazin durchsuchen"
                               :en "Search offers, company area and magazine"}))
             :placeholder (if @(rf/subscribe [::feature/feature-flag-enabled?
                                              global-search/cross-area-search-flag])
                            (t {:de "Angebote, Firmenbereich und Magazin durchsuchen"
                                :en "Search offers, company area and magazine"})
                            (t {:de "Alle Angebote durchsuchen"
                                :en "Browse all offers"}))
             :on-key-up #(if (= (.-key %) "Enter")
                           (do
                             (rf/dispatch-sync [::search-globally @search-input :all])
                             (-> % .-target .blur)
                             (reset! dropdown-open? false))
                           (if (str/blank? @search-input)
                             (reset! dropdown-open? false)
                             (reset! dropdown-open? true)))}]
           (when (seq @search-input)
             [:button
              {:aria-label (t {:de "Suchbegriff entfernen"
                               :en "Remove search term"})
               :on-click #(do
                            (reset! search-input "")
                            (rf/dispatch [::events/keyword-filter-changed ""])
                            (.focus (.getElementById js/document "global-search-input"))
                            (reset! dropdown-open? false))}])]
          [:button.submit-button
           {:on-click #(do
                         (rf/dispatch-sync [::search-globally @search-input :all])
                         (-> % .-target .blur)
                         (reset! dropdown-open? false))}
           (t {:de "Suchen"
               :en "Search"})]]
         (when has-dropdown?
           [:div.search-bar-dropdown {:class (when @dropdown-open? "open")
                                      :on-mouse-enter #(reset! mouse-on-dropdown? true)
                                      :on-mouse-leave #(reset! mouse-on-dropdown? false)}
            [:button.search-bar-dropdown__content
             {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id global-search-bar-ph-ui-id "selection" "all")
              :on-click #(do
                           (rf/dispatch-sync [::search-globally @search-input :all])
                           (reset! dropdown-open? false))}
             (t {:de "Alle anzeigen"
                 :en "View all"})]
            [:button.search-bar-dropdown__content
             {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id global-search-bar-ph-ui-id "selection" "machtfit")
              :on-click #(do
                           (rf/dispatch-sync [::search-globally @search-input :machtfit])
                           (reset! dropdown-open? false))}
             (t {:de "machtfit-Angebote"
                 :en "machtfit offers"})]
            [:button.search-bar-dropdown__content
             {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id global-search-bar-ph-ui-id "selection" "company")
              :on-click #(do
                           (rf/dispatch-sync [::search-globally @search-input :company])
                           (reset! dropdown-open? false))}
             (t @(rf/subscribe [:get [:ui :company-area-name]]))]])])})))

(rf/reg-sub
  ::favorites-count
  :<- [:get [:user :favorites]]
  (fn [favs _]
    (count favs)))

(defn- favorites-button []
  (let [favorites-count @(rf/subscribe [::favorites-count])
        {:keys [url route-data]} (get statics/pages :favorites)
        href @(rf/subscribe [:get [:urls url]])]
    [:a.flex.items-center.justify-center.relative.rounded-full.transition-colors.duration-200.ease-in-out
     (merge
      {:aria-label (t {:de "Favoriten" :en "Favorites"})
       :class "hover:bg-[#f3f3f3] w-[60px] h-[40px] rounded-[24px]"
       :data-ph-capture-attribute-ui_id "header__favorites"}
      (when route-data (routes/attrs-link-to-page route-data))
      (when href {:href href}))

     [icon/outline
      "heart"
      {:width "w-[24px]"
       :height "h-[24px]"
       :class "stroke-[1.5]"}]
     (when (pos-int? favorites-count)
       [:span.absolute.flex.items-center.justify-center.rounded-full.bg-brand-500.text-white.font-bold
        {:style {:width "20px" :height "20px" :font-size "12px" :top "2px" :right "6px"}}
        favorites-count])]))

(rf/reg-event-db
  ::toggle-user-menu
  (fn [db]
    (update-in db [:ui :user-menu-open?] not)))

(defn breadcrumb [{:keys [label href id]} last?]
  (if last?
    [:span.last-breadcrumb (t label)]
    [:a {:href href
         :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "breadcrumb" id)
         :on-click (fn [event]
                     (when event (.preventDefault event))
                     (rf/dispatch [::routes/navigate routes/router {:href href}]))}
     (t label)]))

(defn breadcrumbs-component []
  (let [breadcrumbs @(rf/subscribe [:get [:ui :breadcrumbs]])]
    (when (> (count breadcrumbs) 1)
      [:div.breadcrumbs-container.container
       (conj
        [:div.breadcrumbs
         (doall
          (for [breadcrumb-map (pop breadcrumbs)]
            ^{:key (:de (:label breadcrumb-map))}
            [breadcrumb breadcrumb-map false]))]
        [breadcrumb (peek breadcrumbs) true])])))

(defn mobile-breadcrumbs-component []
  (let [breadcrumbs @(rf/subscribe [:get [:ui :breadcrumbs]])]
    (when-let [penultimate-breadcrumb (and (seq breadcrumbs)
                                           (peek (pop breadcrumbs)))]
      [:div.mobile-breadcrumbs-container.container
       [:div.breadcrumbs
        [breadcrumb penultimate-breadcrumb false]]])))

(defn header []
  (let [balance @(rf/subscribe [:get [:user :balance]])
        is-employee? @(rf/subscribe [:get [:user :is-employee?]])
        authenticated? @(rf/subscribe [:get [:user :authenticated?]])
        show-bgm-info? @(rf/subscribe [:get [:user :show-bgm-info?]])
        has-health-expense? @(rf/subscribe [:get [:user :has-health-expense?]])
        has-health-expense-history? @(rf/subscribe [:get [:user :has-health-expense-history?]])
        open-user-menu? @(rf/subscribe [:get [:ui :user-menu-open?]])
        has-budget-period? @(rf/subscribe [:get [:user :has-budget-period?]])
        display-balance (if has-budget-period?
                          (or balance 0)
                          balance)
        remaining-sachbezug-budget @(rf/subscribe [::remaining-sachbezug-budget])
        provider-infos @(rf/subscribe [:get [:user :current-providers-for-user]])
        has-memberships? (seq provider-infos)]
    [:header.header-container
     [:div.container.container-xl.header
      [:a.header__logo
       (cond-> (routes/attrs-link-to-page {:id :home})
         (not authenticated?) (assoc :class ["pb-4"]))
       [layout/subdomain-logo]]
      (when authenticated?
        [:<>
         [:div.header__flex
          [navigation/navigation]
          (when is-employee?
            [global-search-bar @(rf/subscribe [:get [:filter]])])]
         [:div.header__utilities
          (when (and is-employee? display-balance)
            [:a.header__budget
             {:href @(rf/subscribe [:get [:urls (get-in statics/external-pages [:profile-view :url])]])
              :style {:text-decoration "none"
                      :cursor "pointer"}
              :on-click (comp open-profile-page
                              machtfit/stop-event)}
             [:div.flex.gap-1.items-center
              {:class "gap-[4px]"}
              [icon/outline "coins-02" {:width "w-[20px]"
                                        :stroke "stroke-gray-800"
                                        :height "h-[20px]"}]
              [:strong.text-gray-800 {:class "text-[16px]"}
               (str/replace (format/currency display-balance) "€" "EUR ")]]
             (when remaining-sachbezug-budget
               [:div.header__budget-comment
                "(" [t {:de [:<>
                             [:span
                              "diesen Monat " remaining-sachbezug-budget " €"]
                             " für nicht-präventive Angebote"]
                        :en [:<>
                             [:span
                              "€" remaining-sachbezug-budget
                              " this month"]
                             " for non-preventive offers"]}] ")"])])
          [favorites-button]
          [:div.header__dropdown
           [:button.header__icon-link
            {:aria-label (t {:de "Menü"
                             :en "Menu"})
             :on-click (fn [e]
                         (.stopPropagation e)
                         (rf/dispatch [::toggle-user-menu]))}
            [icons/header-profile]]
           (when open-user-menu?
             [:nav.header__dropdown-content
              [:ul
               (when (= @(rf/subscribe [:get [:subdomain]]) "gesundheitplus")
                 [:li [:a.header__hochbahn.nav-featured-link {:href "http://hochbahn-direkt.de/"
                                                              :target "_blank"
                                                              :style {:color "#e2011f"}}
                       [:i.glyphicon.glyphicon-share]
                       "HOCHBAHN-Portal"]])
               (when is-employee?
                 [:<>
                  (when display-balance
                    [:li [:a.flex.sm:!hidden.items-center
                          {:class "gap-[10px]"
                           :href @(rf/subscribe [:get [:urls (get-in statics/external-pages [:profile-view :url])]])
                           :on-click (comp open-profile-page machtfit/stop-event)}
                          [icon/outline "coins-02" {:width "w-[20px]"
                                                    :stroke "stroke-gray-600"
                                                    :height "h-[20px]"}]
                          [t {:de [:<> [:strong "Mein Guthaben: "]
                                   [:span.text-gray-600 (str/replace (format/currency display-balance) "€" "EUR ")]]
                              :en [:<> [:strong "My Balance: "]
                                   [:span.text-gray-600 (str/replace (format/currency display-balance) "€" "EUR ")]]}]]])
                  [:li [user-menu-link :account-health-profile {:featured? true}]]
                  [:li [user-menu-link :account-activity {:featured? true}]]
                  [:li [user-menu-link :order-list {:featured? true}]]
                  (when has-memberships?
                    [:li [user-menu-link :memberships {:featured? true}]])
                  (when (or has-health-expense? has-health-expense-history?)
                    [:li [user-menu-link :health-expenses {:featured? true}]])
                  [:li [user-menu-link :favorites {:featured? true}]]
                  (when show-bgm-info?
                    [:li [user-menu-link :bgm-info {:featured? true}]])
                  [:hr.header__dropdown-content-divider]
                  [:li [user-menu-link :profile-view]]

                  [:li [user-menu-link :tell-a-colleague]]
                  (when @(rf/subscribe [:get [:user :has-cockpit-access?]])
                    [:li [user-menu-link :cockpit]])
                  [:hr.header__dropdown-content-divider]])
               [:li [user-menu-link :help]]
               [:li [user-menu-link :logout]]]])]]])]]))
